/**
 * Deactivate User Modal Component
 *
 * Confirmation modal for deactivating a tenant user.
 *
 * @fileoverview Deactivate user confirmation modal
 */

import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Text,
  Avatar,
  HStack,
  Box,
  useColorModeValue,
  Alert,
  AlertIcon,
  AlertDescription,
} from '@chakra-ui/react';
import type { TenantUser } from '../../types/users';

interface DeactivateUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: TenantUser | null;
  onConfirm: () => Promise<void>;
  loading: boolean;
}

/**
 * Deactivate User Modal Component
 *
 * Confirmation dialog for deactivating a tenant user.
 */
export const DeactivateUserModal: React.FC<DeactivateUserModalProps> = ({
  isOpen,
  onClose,
  user,
  onConfirm,
  loading,
}) => {
  const bgColor = useColorModeValue('white', 'xacto.deepNavy');
  const textColor = useColorModeValue('gray.900', 'xacto.pureWhite');
  const mutedColor = useColorModeValue('gray.500', 'xacto.silverGray');
  // Hoisted above the `if (!user) return null` below: it used to be called
  // inline in the JSX, only reached while `user` is set — since this modal
  // stays mounted with `user` toggling between an object and null as the
  // caller opens/closes it, that made the hook count for this instance
  // change between renders (React: "has detected a change in the order of
  // Hooks").
  const userCardBg = useColorModeValue('gray.50', 'xacto.darkSurface');

  const getDisplayName = (u: TenantUser) => {
    if (u.user.displayName) return u.user.displayName;
    if (u.user.firstName || u.user.lastName) {
      return `${u.user.firstName || ''} ${u.user.lastName || ''}`.trim();
    }
    return u.user.email.split('@')[0];
  };

  if (!user) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent bg={bgColor}>
        <ModalHeader>Deactivate User</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <Alert status="warning" borderRadius="md" mb={4}>
            <AlertIcon />
            <AlertDescription>
              Deactivating a user will prevent them from signing in. They can be reactivated later.
            </AlertDescription>
          </Alert>

          <Text color={textColor} mb={4}>
            Are you sure you want to deactivate this user?
          </Text>

          <HStack spacing={3} p={3} borderRadius="md" bg={userCardBg}>
            <Avatar size="md" name={getDisplayName(user)} />
            <Box>
              <Text fontWeight="medium" color={textColor}>
                {getDisplayName(user)}
              </Text>
              <Text fontSize="sm" color={mutedColor}>
                {user.user.email}
              </Text>
            </Box>
          </HStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={loading}>
            Cancel
          </Button>
          <Button
            colorScheme="orange"
            onClick={onConfirm}
            isLoading={loading}
            loadingText="Deactivating..."
          >
            Deactivate User
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default DeactivateUserModal;
